import {
  type ComputedRef,
  type Ref,
  type WatchSource,
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue'

/** Where a highlight sits inside its container, in px. */
export interface HighlightBox {
  top: number
  height: number
}

/** Quiet time, in ms, after the last layout change before a tracking highlight may travel again. */
const TRACKING_SETTLE_MS = 120

/** Measures `element` inside `container` by summing offsets, so transforms and entrance animations never skew the result. */
export function offsetWithin(element: HTMLElement, container: HTMLElement): HighlightBox {
  let top = 0
  let node: HTMLElement | null = element
  while (node && node !== container) {
    top += node.offsetTop
    node = node.offsetParent instanceof HTMLElement ? node.offsetParent : null
  }
  return { top, height: element.offsetHeight }
}

/** The inline style that places a highlight element on its box. */
export function highlightStyle(box: HighlightBox): { translate: string; height: string } {
  return { translate: `0 ${box.top}px`, height: `${box.height}px` }
}

export interface TravellingHighlightOptions {
  /** The positioned element the highlight is placed in. */
  container: () => HTMLElement | null
  /** The row the highlight rests on, or null when there is none. */
  target: () => HTMLElement | null
  /** Re-measures whenever one of these changes, after the DOM has updated. */
  track?: WatchSource | WatchSource[]
  /** An element whose size changes move the rows; the highlight is re-measured on each and, if `suspendWhileMoving` is set, stops travelling meanwhile. */
  observe?: () => Element | null
  /** Keeps the highlight glued to its row, with no travel, while the observed element is changing size. */
  suspendWhileMoving?: boolean
}

export interface TravellingHighlight {
  box: Ref<HighlightBox>
  /** The inline style for the highlight element. */
  style: ComputedRef<{ translate: string; height: string }>
  /** Whether the highlight may travel; off until the first paint has landed. */
  travels: Ref<boolean>
  /** Whether the layout is moving now and the highlight must follow it without travelling. */
  tracking: Ref<boolean>
  measure: () => void
  enableTravel: () => void
  disableTravel: () => void
  /** Measures now, then turns travel on after two frames, so the first paint lands without sliding in. */
  landThenTravel: () => void
}

/** Measures the row a highlight rests on and tells the highlight when it may travel to a new one. */
export function useTravellingHighlight(options: TravellingHighlightOptions): TravellingHighlight {
  const box = ref<HighlightBox>({ top: 0, height: 0 })
  const travels = ref(false)
  const tracking = ref(false)
  const style = computed(() => highlightStyle(box.value))

  function measure(): void {
    const container = options.container()
    const target = options.target()
    if (!container || !target) return
    box.value = offsetWithin(target, container)
  }

  function enableTravel(): void {
    travels.value = true
  }
  function disableTravel(): void {
    travels.value = false
  }

  let frame = 0
  function landThenTravel(): void {
    measure()
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(enableTravel)
    })
  }

  if (options.track) {
    watch(options.track, () => void nextTick(measure))
  }

  let observer: ResizeObserver | null = null
  let settleTimer: ReturnType<typeof setTimeout> | undefined

  function onResize(): void {
    measure()
    if (!options.suspendWhileMoving) return
    tracking.value = true
    clearTimeout(settleTimer)
    settleTimer = setTimeout(() => {
      tracking.value = false
    }, TRACKING_SETTLE_MS)
  }

  watch(
    () => options.observe?.() ?? null,
    (observed) => {
      observer?.disconnect()
      observer = null
      if (!observed || typeof ResizeObserver === 'undefined') return
      observer = new ResizeObserver(onResize)
      observer.observe(observed)
    },
    { flush: 'post' },
  )

  onBeforeUnmount(() => {
    observer?.disconnect()
    cancelAnimationFrame(frame)
    clearTimeout(settleTimer)
  })

  return { box, style, travels, tracking, measure, enableTravel, disableTravel, landThenTravel }
}
